import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';

const SubmitBid = ({ currentUserId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const campaign = location.state?.campaign;

  const [bidData, setBidData] = useState({
    platform: campaign?.platform || '',
    followers: '',
    proposedAmount: '',
    message: '',
    whatsappNumber: '',
    profilePhotoUrl: '',
    socialMediaHandles: {
      instagram: '',
      tiktok: '',
      facebook: '',
      twitter: ''
    }
  });

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 3MB)
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Photo size must be less than 3MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setBidData({ ...bidData, profilePhotoUrl: reader.result });
      toast.success('Profile photo uploaded successfully');
    };
    reader.onerror = () => {
      toast.error('Failed to read photo');
    };
    reader.readAsDataURL(file);
  };

  if (!campaign) {
    navigate('/influencer-portal');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!bidData.platform || !bidData.followers || !bidData.proposedAmount || !bidData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!bidData.profilePhotoUrl) {
      toast.error('Please upload your profile photo');
      return;
    }

    try {
      const bid = {
        campaignId: campaign.id,
        influencerId: currentUserId,
        platform: bidData.platform,
        followers: parseInt(bidData.followers),
        proposedAmount: parseFloat(bidData.proposedAmount),
        message: bidData.message,
        whatsappNumber: bidData.whatsappNumber,
        socialMediaHandles: bidData.socialMediaHandles,
        profilePhotoUrl: bidData.profilePhotoUrl
      };

      await axios.post(`${API}/bids`, bid);
      toast.success('Bid submitted successfully!');
      navigate('/influencer-portal');
    } catch (error) {
      console.error('Error submitting bid:', error);
      toast.error('Failed to submit bid');
    }
  };

  const formatZAR = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <main className="max-w-4xl mx-auto px-4 py-8">
      <Button onClick={() => navigate('/influencer-portal')} variant="ghost" className="mb-4">
        <i className="fas fa-arrow-left mr-2"></i> Back to Campaigns
      </Button>

      <Card>
        <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50">
          <CardTitle className="text-2xl" style={{ fontFamily: 'Playfair Display, serif' }}>
            Submit Your Bid
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {/* Campaign Info */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 className="font-semibold text-lg mb-3 text-gray-800">Campaign Details</h3>
            <div className="space-y-2">
              <p className="text-sm"><span className="font-semibold">Title:</span> {campaign.title}</p>
              <p className="text-sm"><span className="font-semibold">Platform:</span> <span className="text-red-600 font-bold">{campaign.platform}</span></p>
              <p className="text-sm"><span className="font-semibold">Budget:</span> {formatZAR(campaign.budget)}</p>
              <p className="text-sm"><span className="font-semibold">Min. Followers:</span> {campaign.minFollowers.toLocaleString()}</p>
              <p className="text-sm"><span className="font-semibold">Duration:</span> {campaign.duration === 'day' ? '1 Day' : campaign.duration === 'week' ? '1 Week' : '1 Month'}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="platform">Platform *</Label>
                <Input
                  id="platform"
                  value={bidData.platform}
                  onChange={(e) => setBidData({ ...bidData, platform: e.target.value })}
                  placeholder="e.g., Instagram"
                  required
                  data-testid="bid-platform-input"
                />
              </div>

              <div>
                <Label htmlFor="followers">Your Followers Count *</Label>
                <Input
                  id="followers"
                  type="number"
                  value={bidData.followers}
                  onChange={(e) => setBidData({ ...bidData, followers: e.target.value })}
                  placeholder="50000"
                  required
                  data-testid="bid-followers-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="proposedAmount">Proposed Amount (ZAR) *</Label>
              <Input
                id="proposedAmount"
                type="number"
                value={bidData.proposedAmount}
                onChange={(e) => setBidData({ ...bidData, proposedAmount: e.target.value })}
                placeholder="25000"
                required
                data-testid="bid-amount-input"
              />
              <p className="text-xs text-gray-500 mt-1">Campaign budget: {formatZAR(campaign.budget)}</p>
            </div>

            <div>
              <Label htmlFor="message">Proposal Message *</Label>
              <Textarea
                id="message"
                value={bidData.message}
                onChange={(e) => setBidData({ ...bidData, message: e.target.value })}
                placeholder="Describe why you're a great fit for this campaign. Mention your audience demographics, engagement rates, and previous successful campaigns."
                rows={6}
                required
                data-testid="bid-message-input"
              />
            </div>

            {/* Profile Photo Upload */}
            <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
              <Label htmlFor="profilePhoto" className="text-purple-800 font-semibold">
                <i className="fas fa-user-circle mr-2"></i>Profile Photo *
              </Label>
              <Input
                id="profilePhoto"
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="mt-2"
                required
                data-testid="bid-photo-input"
              />
              <p className="text-xs text-purple-600 mt-1">
                <i className="fas fa-info-circle mr-1"></i>
                Upload a clear profile photo to help agents recognize you (max 3MB)
              </p>
              {bidData.profilePhotoUrl && (
                <div className="mt-3 flex items-center gap-3">
                  <img 
                    src={bidData.profilePhotoUrl} 
                    alt="Profile preview" 
                    className="w-20 h-20 rounded-full object-cover border-2 border-purple-300"
                  />
                  <p className="text-sm text-green-600">
                    <i className="fas fa-check-circle mr-1"></i> Photo uploaded successfully
                  </p>
                </div>
              )}
            </div>

            <Card className="bg-blue-50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-lg">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="whatsappNumber">WhatsApp Number</Label>
                  <Input
                    id="whatsappNumber"
                    value={bidData.whatsappNumber}
                    onChange={(e) => setBidData({ ...bidData, whatsappNumber: e.target.value })}
                    placeholder="+27 12 345 6789"
                    data-testid="bid-whatsapp-input"
                  />
                  <p className="text-xs text-gray-600 mt-1">Optional - Agents can contact you directly</p>
                </div>

                <div>
                  <Label className="mb-2 block">Social Media Handles</Label>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <i className="fab fa-instagram text-pink-600 text-xl w-8"></i>
                      <Input
                        placeholder="Instagram handle (without @)"
                        value={bidData.socialMediaHandles.instagram}
                        onChange={(e) => setBidData({ 
                          ...bidData, 
                          socialMediaHandles: { ...bidData.socialMediaHandles, instagram: e.target.value }
                        })}
                        data-testid="bid-instagram-input"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="fab fa-tiktok text-black text-xl w-8"></i>
                      <Input
                        placeholder="TikTok handle (without @)"
                        value={bidData.socialMediaHandles.tiktok}
                        onChange={(e) => setBidData({ 
                          ...bidData, 
                          socialMediaHandles: { ...bidData.socialMediaHandles, tiktok: e.target.value }
                        })}
                        data-testid="bid-tiktok-input"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="fab fa-facebook text-blue-600 text-xl w-8"></i>
                      <Input
                        placeholder="Facebook page/profile"
                        value={bidData.socialMediaHandles.facebook}
                        onChange={(e) => setBidData({ 
                          ...bidData, 
                          socialMediaHandles: { ...bidData.socialMediaHandles, facebook: e.target.value }
                        })}
                        data-testid="bid-facebook-input"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="fab fa-twitter text-blue-400 text-xl w-8"></i>
                      <Input
                        placeholder="Twitter/X handle (without @)"
                        value={bidData.socialMediaHandles.twitter}
                        onChange={(e) => setBidData({ 
                          ...bidData, 
                          socialMediaHandles: { ...bidData.socialMediaHandles, twitter: e.target.value }
                        })}
                        data-testid="bid-twitter-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">Optional - Help agents verify your profiles</p>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-4 pt-4">
              <Button type="submit" className="flex-1 bg-red-600 hover:bg-red-700" data-testid="submit-bid-btn">
                <i className="fas fa-paper-plane mr-2"></i> Submit Bid
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/influencer-portal')}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </main>
  );
};

export default SubmitBid;
